import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = 'AIzaSyBlcOaGsV6wV6u-bd1onOicoZPBUbX8RE8';
const API_BASE = 'https://weridesudan.com/api/admin';
const DEFAULT_CENTER = { lat: 15.5007, lng: 32.5599 };
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };

interface Driver {
  _id: string; name: string; phone: string; isAvailable: boolean;
  location: { coordinates: [number, number] };
}
interface Ride {
  _id: string; status: string;
  pickup: { coordinates?: [number, number]; address?: string };
  dropoff: { coordinates?: [number, number]; address?: string };
  rider?: { name: string }; driver?: { name: string };
}

const LiveMap: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [rides, setRides] = useState<Ride[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState('');

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE}/map-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setDrivers(data.drivers || []);
      setRides(data.activeRides || []);
      setLastUpdated(new Date().toLocaleTimeString());
      setError('');
    } catch {
      setError('Could not load map data');
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 15000);
    return () => clearInterval(id);
  }, [fetchData]);

  const ll = (coords?: [number, number]) => {
    if (!coords || coords.length < 2) return null;
    return { lat: coords[1], lng: coords[0] };
  };

  const validDrivers = drivers.filter(d => d.location?.coordinates?.length === 2);
  const validRides = rides.filter(r => r.pickup?.coordinates?.length === 2 && r.dropoff?.coordinates?.length === 2);

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 20 }}>
          <span style={{ fontSize: 14 }}>
            <strong style={{ color: '#22c55e' }}>{validDrivers.length}</strong> Online Drivers
          </span>
          <span style={{ fontSize: 14 }}>
            <strong style={{ color: '#f97316' }}>{validRides.length}</strong> Active Rides
          </span>
        </div>
        <span style={{ fontSize: 12, color: error ? '#ef4444' : '#9ca3af' }}>
          {error || `Updated ${lastUpdated}`}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#9ca3af' }}>
        {[['#22c55e','Available driver'],['#f97316','On a ride'],['#3b82f6','Pickup'],['#ef4444','Dropoff']].map(([c,l]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' }} />{l}
          </span>
        ))}
      </div>

      <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden' }}>
        {!isLoaded ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2937', color: '#9ca3af' }}>
            Loading map...
          </div>
        ) : (
          <GoogleMap mapContainerStyle={MAP_CONTAINER_STYLE} center={DEFAULT_CENTER} zoom={12}
            options={{ styles: darkStyle, zoomControl: true, streetViewControl: false, mapTypeControl: false }}>

            {validDrivers.map(d => {
              const pos = ll(d.location.coordinates);
              if (!pos) return null;
              return (
                <Marker key={d._id} position={pos}
                  icon={{ url: d.isAvailable ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' : 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png' }}
                  onClick={() => { setSelectedDriver(d); setSelectedRide(null); }} />
              );
            })}

            {validRides.map(r => {
              const pickup = ll(r.pickup.coordinates);
              const dropoff = ll(r.dropoff.coordinates);
              if (!pickup || !dropoff) return null;
              return (
                <React.Fragment key={r._id}>
                  <Marker position={pickup} icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }} onClick={() => { setSelectedRide(r); setSelectedDriver(null); }} />
                  <Marker position={dropoff} icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }} onClick={() => { setSelectedRide(r); setSelectedDriver(null); }} />
                  <Polyline path={[pickup, dropoff]} options={{ strokeColor: '#6366f1', strokeOpacity: 0.6, strokeWeight: 2 }} />
                </React.Fragment>
              );
            })}

            {selectedDriver && ll(selectedDriver.location.coordinates) && (
              <InfoWindow position={ll(selectedDriver.location.coordinates)!} onCloseClick={() => setSelectedDriver(null)}>
                <div style={{ fontSize: 13, lineHeight: '1.6' }}>
                  <strong>{selectedDriver.name}</strong><br />
                  {selectedDriver.phone}<br />
                  <span style={{ color: selectedDriver.isAvailable ? '#16a34a' : '#ea580c' }}>
                    {selectedDriver.isAvailable ? 'Available' : 'On a ride'}
                  </span>
                </div>
              </InfoWindow>
            )}

            {selectedRide && ll(selectedRide.pickup.coordinates) && (
              <InfoWindow position={ll(selectedRide.pickup.coordinates)!} onCloseClick={() => setSelectedRide(null)}>
                <div style={{ fontSize: 13, lineHeight: '1.6' }}>
                  <strong>Ride — {selectedRide.status.replace('_', ' ')}</strong><br />
                  {selectedRide.rider && <>{selectedRide.rider.name}<br /></>}
                  {selectedRide.driver && <>{selectedRide.driver.name}<br /></>}
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    {selectedRide.pickup.address || 'Pickup'} → {selectedRide.dropoff.address || 'Dropoff'}
                  </span>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>
    </div>
  );
};

const darkStyle = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];

export default LiveMap;